import React, {useState, useEffect, useRef} from 'react';
import { API_URL } from '../config/apiConfig';
import {useNavigate, Link} from 'react-router-dom';
import {styled, useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    Box,
    Button,
    Checkbox,
    CssBaseline,
    FormControl,
    FormControlLabel,
    FormLabel,
    TextField,
    Typography,
    Stack,
    Divider,
    Alert,
    Paper,
    Grid,
    InputAdornment,
    IconButton,
    CircularProgress,
    MenuItem,
    DialogContentText,
    DialogActions,
    DialogContent,
    Dialog,
    DialogTitle
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Person,
    Email,
    Phone,
    Home,
    Key,
    VerifiedUser,
    CheckCircle
} from '@mui/icons-material';
import AppTheme from '../shared-theme/AppTheme';
import ColorModeSelect from '../shared-theme/ColorModeSelect';
import { calculatePasswordStrength, PasswordRequirements, PasswordStrengthIndicator } from '../utils/PasswordStrength';

import ContractTermsModal from './ContractTermsModal';

const Card = styled(Paper)(({theme}) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 32px rgba(0, 0, 0, 0.5)'
        : '0 8px 32px rgba(0, 0, 0, 0.1)',
    borderRadius: '16px',
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
    '&:hover': {
        boxShadow: theme.palette.mode === 'dark'
            ? '0 12px 40px rgba(0, 0, 0, 0.7)'
            : '0 12px 40px rgba(0, 0, 0, 0.15)',
    },
    [theme.breakpoints.up('md')]: {
        maxWidth: '900px',
    },
}));

const SignUpContainer = styled(Stack)(({theme}) => ({
    minHeight: '100vh',
    background: theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
        : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
    },
}));

const StyledFormControl = styled(FormControl)(({theme}) => ({
    marginBottom: theme.spacing(2),
}));

const StyledButton = styled(Button)(({theme}) => ({
    padding: '12px 0',
    borderRadius: '25px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '1rem',
    backgroundColor: props => props.disabled ? '#c0c0c0' : '#FFD700',
    color: props => props.disabled ? '#777777' : '#777777',
    transition: 'all 0.2s ease-in-out',
    boxShadow: 'none',
    '&:hover': {
        backgroundColor: props => props.disabled ? '#c0c0c0' : '#FFD700',
        opacity: props => props.disabled ? 1 : 0.9,
        transform: props => props.disabled ? 'none' : 'translateY(-2px)',
        boxShadow: props => props.disabled ? 'none' : '0 4px 8px rgba(0, 0, 0, 0.1)',
    },
}));

export default function JoinUsForm() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Form state
    const [fullName, setFullName] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [email, setEmail] = useState('');
    const [confirmEmail, setConfirmEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [termsModalOpen, setTermsModalOpen] = useState(false);
    const [termsId, setTermsId] = useState('');
    const [isAffiliateOwner, setIsAffiliateOwner] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Validation error states
    const [emailError, setEmailError] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState('');
    const [confirmEmailError, setConfirmEmailError] = useState(false);
    const [confirmEmailErrorMessage, setConfirmEmailErrorMessage] = useState('');
    const [passwordError, setPasswordError] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState(false);
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState('');
    const [fullNameError, setFullNameError] = useState(false);
    const [fullNameErrorMessage, setFullNameErrorMessage] = useState('');

    const [phoneError, setPhoneError] = useState(false);
    const [phoneErrorMessage, setPhoneErrorMessage] = useState('');

    const [notificationOpen, setNotificationOpen] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");

    const [sex, setSex] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [sexError, setSexError] = useState(false);
    const [sexErrorMessage, setSexErrorMessage] = useState('');
    const [dateOfBirthError, setDateOfBirthError] = useState(false);
    const [dateOfBirthErrorMessage, setDateOfBirthErrorMessage] = useState('');

    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        level: 'none',
        color: '#gray',
        text: 'Enter password',
        timeToHack: '',
        requirements: {
            length: false,
            uppercase: false,
            lowercase: false,
            number: false,
            symbol: false
        }
    });
    const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
    const [captchaToken, setCaptchaToken] = useState('');
    const [captchaReady, setCaptchaReady] = useState(false);
    const captchaContainerRef = useRef(null);
    const captchaWidgetIdRef = useRef(null);

    // API URL
    // API_URL imported from config

    // Basic email format validation
    const validateBasicEmailFormat = (emailToValidate) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(emailToValidate);
    };

    // Uuendage validatePassword funktsiooni
    const validatePassword = (passwordToValidate) => {
        // Arvutame parooli tugevuse
        const strength = calculatePasswordStrength(passwordToValidate);
        setPasswordStrength(strength);

        if (!passwordToValidate || passwordToValidate.length < 12) {
            return {
                valid: false,
                message: 'Password must be at least 12 characters long.'
            };
        }

        const hasUpperCase = /[A-Z]/.test(passwordToValidate);
        const hasLowerCase = /[a-z]/.test(passwordToValidate);
        const hasNumber = /[0-9]/.test(passwordToValidate);

        if (!(hasUpperCase && hasLowerCase && hasNumber)) {
            return {
                valid: false,
                message: 'Password must contain at least one lowercase letter, one uppercase letter, and one number.'
            };
        }

        // Kontrollime, kas parool on piisavalt tugev
        if (strength.score < 3) {
            return {
                valid: false,
                message: 'Password is too weak. Please choose a stronger password.'
            };
        }

        return {
            valid: true,
            message: ''
        };
    };

    useEffect(() => {
        if (password) {
            const strength = calculatePasswordStrength(password);
            setPasswordStrength(strength);
        } else {
            setPasswordStrength({
                score: 0,
                level: 'none',
                color: '#gray',
                text: 'Enter password',
                timeToHack: '',
                requirements: {
                    length: false,
                    uppercase: false,
                    lowercase: false,
                    number: false
                }
            });
        }
    }, [password]);

    useEffect(() => {
        if (!turnstileSiteKey) return undefined;

        let isActive = true;
        let pollId = null;

        const renderTurnstile = () => {
            if (!isActive || !captchaContainerRef.current || !window.turnstile) return;
            if (captchaWidgetIdRef.current !== null) return;

            captchaWidgetIdRef.current = window.turnstile.render(captchaContainerRef.current, {
                sitekey: turnstileSiteKey,
                theme: theme.palette.mode === 'dark' ? 'dark' : 'light',
                callback: (token) => setCaptchaToken(token),
                'expired-callback': () => setCaptchaToken(''),
                'error-callback': () => setCaptchaToken(''),
            });
            setCaptchaReady(true);
        };

        const ensureScript = () => {
            if (window.turnstile) {
                renderTurnstile();
                return;
            }

            const src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
            const existingScript = document.querySelector(`script[src="${src}"]`);
            if (!existingScript) {
                const script = document.createElement('script');
                script.src = src;
                script.async = true;
                script.defer = true;
                document.head.appendChild(script);
            }

            pollId = window.setInterval(() => {
                if (window.turnstile) {
                    window.clearInterval(pollId);
                    renderTurnstile();
                }
            }, 250);
        };

        ensureScript();

        return () => {
            isActive = false;
            if (pollId) window.clearInterval(pollId);
        };
    }, [turnstileSiteKey, theme.palette.mode]);

    const resetCaptchaWidget = () => {
        setCaptchaToken('');
        if (window.turnstile && captchaWidgetIdRef.current !== null) {
            window.turnstile.reset(captchaWidgetIdRef.current);
        }
    };

    const normalizeEmail = (email) => {
        if (!email) return '';

        // Kontrolli, kas tegemist on Gmail aadressiga
        const [localPart, domain] = email.toLowerCase().split('@');

        if (domain === 'gmail.com') {
            // Eemalda punktid ja kõik pärast + märki
            const normalizedLocalPart = localPart.replace(/\./g, '').split('+')[0];
            return `${normalizedLocalPart}@${domain}`;
        }

        // Mitte-Gmaili aadresside puhul muuda lihtsalt väiketähtedeks
        return email.toLowerCase();
    };

    // Phone validation - checks for length and valid format
    const validatePhoneNumber = (phoneToValidate) => {
        // Check if empty
        if (!phoneToValidate) {
            return {
                valid: false,
                message: 'Phone number is required.'
            };
        }

        // Check length (6-20 characters)
        if (phoneToValidate.length < 6 || phoneToValidate.length > 20) {
            return {
                valid: false,
                message: 'Phone number must be between 6-20 characters long.'
            };
        }

        // Basic phone format validation - allows for various international formats
        // This regex allows +, spaces, dashes, parentheses, and numbers
        const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,3}[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,4}$/;
        if (!phoneRegex.test(phoneToValidate)) {
            return {
                valid: false,
                message: 'Please enter a valid phone number.'
            };
        }

        return {
            valid: true,
            message: ''
        };
    };

    // Check if emails match and update relevant states
    const checkEmailsMatch = () => {
        if (confirmEmail) {
            if (email !== confirmEmail) {
                setConfirmEmailError(true);
                setConfirmEmailErrorMessage('Emails do not match');
            } else {
                setConfirmEmailError(false);
                setConfirmEmailErrorMessage('');
            }
        } else {
            // If confirmEmail is empty, we don't show an error yet
            setConfirmEmailError(false);
            setConfirmEmailErrorMessage('');
        }
    };

    // Validate confirm email whenever either email or confirm email changes
    useEffect(() => {
        checkEmailsMatch();
    }, [email, confirmEmail]);

    const validateInputs = () => {
        let isValid = true;

        // Full Name validation (updated)
        const nameValidation = validateFullName(fullName);
        if (!nameValidation.valid) {
            setFullNameError(true);
            setFullNameErrorMessage(nameValidation.message);
            isValid = false;
        } else {
            setFullNameError(false);
            setFullNameErrorMessage('');
        }

        // Basic Email validation
        if (!email || !validateBasicEmailFormat(email)) {
            setEmailError(true);
            setEmailErrorMessage('Please enter a valid email address.');
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        // Confirm email validation
        if (!confirmEmail || confirmEmail !== email) {
            setConfirmEmailError(true);
            setConfirmEmailErrorMessage('Emails do not match.');
            isValid = false;
        } else {
            setConfirmEmailError(false);
            setConfirmEmailErrorMessage('');
        }

        // Password validation (enhanced)
        const passwordValidation = validatePassword(password);
        if (!passwordValidation.valid) {
            setPasswordError(true);
            setPasswordErrorMessage(passwordValidation.message);
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        // Confirm password validation
        if (!confirmPassword || confirmPassword !== password) {
            setConfirmPasswordError(true);
            setConfirmPasswordErrorMessage('Passwords do not match.');
            isValid = false;
        } else {
            setConfirmPasswordError(false);
            setConfirmPasswordErrorMessage('');
        }

        // Phone validation
        const phoneValidation = validatePhoneNumber(phone);
        if (!phoneValidation.valid) {
            setPhoneError(true);
            setPhoneErrorMessage(phoneValidation.message);
            isValid = false;
        } else {
            setPhoneError(false);
            setPhoneErrorMessage('');
        }

        const sexValidation = validateSex(sex);
        if (!sexValidation.valid) {
            setSexError(true);
            setSexErrorMessage(sexValidation.message);
            isValid = false;
        } else {
            setSexError(false);
            setSexErrorMessage('');
        }

        const dateOfBirthValidation = validateDateOfBirth(dateOfBirth);
        if (!dateOfBirthValidation.valid) {
            setDateOfBirthError(true);
            setDateOfBirthErrorMessage(dateOfBirthValidation.message);
            isValid = false;
        } else {
            setDateOfBirthError(false);
            setDateOfBirthErrorMessage('');
        }

        // Terms acceptance validation
        if (!acceptedTerms) {
            setError('You must accept the terms and conditions');
            isValid = false;
        } else {
            setError('');
        }

        return isValid;
    };

    const validateSex = (sexToValidate) => {
        if (!sexToValidate) {
            return {
                valid: false,
                message: 'Sex is required.'
            };
        }

        const validOptions = ['male', 'female', 'other'];
        if (!validOptions.includes(sexToValidate)) {
            return {
                valid: false,
                message: 'Please select a valid option.'
            };
        }

        return {
            valid: true,
            message: ''
        };
    };

    const validateDateOfBirth = (dateToValidate) => {
        if (!dateToValidate) {
            return {
                valid: false,
                message: 'Date of birth is required.'
            };
        }

        const birthDate = new Date(dateToValidate);
        const today = new Date();

        // Kontrolli, kas kuupäev on kehtiv
        if (isNaN(birthDate.getTime())) {
            return {
                valid: false,
                message: 'Please enter a valid date.'
            };
        }

        // Kontrolli, kas kuupäev ei ole tulevikus
        if (birthDate > today) {
            return {
                valid: false,
                message: 'Date of birth cannot be in the future.'
            };
        }

        // Kontrolli minimaalne vanus (16 aastat)
        const minDate = new Date();
        minDate.setFullYear(today.getFullYear() - 16);

        if (birthDate > minDate) {
            return {
                valid: false,
                message: 'You cannot create an account if you are under 16 years old.'
            };
        }

        // Kontrolli maksimaalne vanus (120 aastat)
        const maxDate = new Date();
        maxDate.setFullYear(today.getFullYear() - 120);

        if (birthDate < maxDate) {
            return {
                valid: false,
                message: 'Please enter a valid date of birth.'
            };
        }

        return {
            valid: true,
            message: ''
        };
    };

    const handleDateOfBirthChange = (value) => {
        setDateOfBirth(value);

        if (!value) {
            setDateOfBirthError(false);
            setDateOfBirthErrorMessage('');
            return;
        }

        const validation = validateDateOfBirth(value);
        setDateOfBirthError(!validation.valid);
        setDateOfBirthErrorMessage(validation.valid ? '' : validation.message);
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsSubmitting(true);

        if (!validateInputs()) {
            setIsSubmitting(false);
            return;
        }

        if (turnstileSiteKey && !captchaToken) {
            setError('Please complete captcha verification.');
            setIsSubmitting(false);
            return;
        }



        try {
            const response = await fetch(`${API_URL}/auth/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    fullName,
                    phone,
                    address,
                    email: email,
                    password,
                    sex,
                    dateOfBirth,
                    affiliateOwner: isAffiliateOwner,
                    isAcceptedTerms: acceptedTerms,
                    captchaToken
                }),
            });

            const data = await response.json();
            if (response.ok) {
                setNotificationMessage(
                    'Registration successful! A confirmation code has been sent to your email. Please check your inbox and enter the code to verify your account before logging in.'
                );

                setNotificationOpen(true);

                // Clear form
                setFullName('');
                setPhone('');
                setAddress('');
                setEmail('');
                setConfirmEmail('');
                setPassword('');
                setConfirmPassword('');
                setAcceptedTerms(false);
                setIsAffiliateOwner(false);
                setSex('');
                setDateOfBirth('');

                // Automatically redirect to login after 5 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 5000);
            } else {
                setError(data.error || 'Something went wrong with registration.');
                if (turnstileSiteKey) resetCaptchaWidget();
            }
        } catch (err) {
            console.error(err);
            setError('Server error, please try again later.');
            if (turnstileSiteKey) resetCaptchaWidget();
        } finally {
            setIsSubmitting(false);
        }
    };

    const openTerms = () => {
        setTermsId('register');
        setTermsModalOpen(true);
    };

    const getCreateAccountBlockerReason = () => {
        if (isSubmitting) return 'Creating account...';

        if (!fullName.trim()) return 'Please enter your full name.';
        if (!email.trim()) return 'Please enter your email address.';
        if (!confirmEmail.trim()) return 'Please confirm your email address.';
        if (!phone.trim()) return 'Please enter your phone number.';
        if (!dateOfBirth) return 'Please select your date of birth.';
        const dateOfBirthValidation = validateDateOfBirth(dateOfBirth);
        if (!dateOfBirthValidation.valid) return dateOfBirthValidation.message;
        if (!sex) return 'Please select your sex.';
        if (!password) return 'Please enter a password.';
        if (!confirmPassword) return 'Please confirm your password.';

        if (email.trim() !== confirmEmail.trim()) return 'Email addresses do not match.';
        if (password !== confirmPassword) return 'Passwords do not match.';
        if (password.length < 12) return 'Password must be at least 12 characters long.';
        if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
            return 'Password must include uppercase, lowercase and a number.';
        }
        if (passwordStrength.score < 3) return 'Password is too weak. Please choose a stronger password.';

        if (!acceptedTerms) return 'Please accept Terms and Conditions to continue.';
        if (turnstileSiteKey && !captchaToken) return 'Please complete captcha verification.';

        return '';
    };

    const createAccountBlockerReason = getCreateAccountBlockerReason();
    const isCreateAccountDisabled = Boolean(createAccountBlockerReason);

    const validateFullName = (nameToValidate) => {
        if (!nameToValidate || nameToValidate.trim().length < 2) {
            return {
                valid: false,
                message: 'Name must be at least 2 characters long.'
            };
        }

        if (nameToValidate.length > 100) {
            return {
                valid: false,
                message: 'Name must be less than 100 characters long.'
            };
        }

        // Enhanced regex to support Estonian, Latvian, Lithuanian, and Finnish characters
        // This covers: ä, ö, ü, õ, š, ž, ā, č, ē, ģ, ī, ķ, ļ, ņ, ū, ą, ę, ė, į, ų, å (and uppercase versions)
        const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿĀ-ŽāčēģīķļņšūžĄąČčĘęĖėĮįŠšŲųŪūŽžÅå\s\-]+$/;

        if (!nameRegex.test(nameToValidate)) {
            return {
                valid: false,
                message: 'Name can only contain letters, spaces, and hyphens (including Baltic and Finnish characters).'
            };
        }

        return {
            valid: true,
            message: ''
        };
    };
    return (
        <AppTheme>
            <CssBaseline enableColorScheme/>
            <ColorModeSelect sx={{position: 'fixed', top: '1rem', right: '1rem', zIndex: 1100}}/>
            <SignUpContainer direction="column" justifyContent="space-between">
                <Card elevation={6}>
                    <Box sx={{textAlign: 'center', mb: 3}}>
                        <Typography
                            component="h1"
                            variant="h4"
                            sx={{
                                fontSize: 'clamp(2rem, 5vw, 2.5rem)',
                                fontWeight: 700,
                            }}
                        >
                            Join Us
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{mt: 1}}>
                            Create an account to get started with IronTrack
                        </Typography>
                    </Box>

                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                borderRadius: '8px',
                                animation: 'fadeIn 0.5s'
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert
                            severity="success"
                            sx={{
                                mb: 3,
                                borderRadius: '8px',
                                animation: 'fadeIn 0.5s'
                            }}
                        >
                            {success}
                        </Alert>
                    )}

                    <Box
                        component="form"
                        onSubmit={handleRegister}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Grid container spacing={3}>
                            {/* Left column */}
                            <Grid item xs={12} md={6}>
                                {/* Full Name */}
                                <StyledFormControl fullWidth>
                                    <FormLabel htmlFor="fullName" sx={{mb: 1, fontWeight: 500}}>
                                        Full Name (required)
                                    </FormLabel>
                                    <TextField
                                        error={fullNameError}
                                        helperText={fullNameErrorMessage}
                                        id="fullName"
                                        name="fullName"
                                        placeholder="John Smith"
                                        autoComplete="name"
                                        required
                                        fullWidth
                                        variant="outlined"
                                        color={fullNameError ? 'error' : 'primary'}
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Person/>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }}
                                    />
                                </StyledFormControl>

                                {/* Email */}
                                <StyledFormControl fullWidth>
                                    <FormLabel htmlFor="email" sx={{mb: 1, fontWeight: 500}}>
                                        Email
                                    </FormLabel>
                                    <TextField
                                        error={emailError}
                                        helperText={emailErrorMessage}
                                        id="email"
                                        type="email"
                                        name="email"
                                        placeholder="john@smith.com"
                                        autoComplete="email"
                                        required
                                        fullWidth
                                        variant="outlined"
                                        color="primary"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Email/>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }}
                                    />
                                </StyledFormControl>

                                {/* Confirm Email */}
                                <StyledFormControl fullWidth>
                                    <FormLabel htmlFor="confirmEmail" sx={{mb: 1, fontWeight: 500}}>
                                        Confirm Email
                                    </FormLabel>
                                    <TextField
                                        error={confirmEmailError}
                                        helperText={confirmEmailErrorMessage}
                                        id="confirmEmail"
                                        type="email"
                                        name="confirmEmail"
                                        placeholder="john@smith.com"
                                        autoComplete="email"
                                        required
                                        fullWidth
                                        variant="outlined"
                                        color={confirmEmailError ? 'error' : confirmEmail && !confirmEmailError ? 'success' : 'primary'}
                                        value={confirmEmail}
                                        onChange={(e) => setConfirmEmail(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Email/>
                                                </InputAdornment>
                                            ),
                                            endAdornment: confirmEmail && (
                                                <InputAdornment position="end">
                                                    {!confirmEmailError && confirmEmail ?
                                                        <VerifiedUser color="success"/> : null}
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }}
                                    />
                                </StyledFormControl>

                                <StyledFormControl fullWidth>
                                    <FormLabel htmlFor="phone" sx={{mb: 1, fontWeight: 500}}>
                                        Phone (required)
                                    </FormLabel>
                                    <TextField
                                        id="phone"
                                        name="phone"
                                        placeholder="+123456789"
                                        autoComplete="tel"
                                        required
                                        fullWidth
                                        variant="outlined"
                                        error={phoneError}
                                        helperText={phoneErrorMessage}
                                        color={phoneError ? 'error' : 'primary'}
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Phone/>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }}
                                    />
                                </StyledFormControl>
                            </Grid>

                            {/* Right column */}
                            <Grid item xs={12} md={6}>
                                {/* Password */}
                                <StyledFormControl fullWidth>
                                    <FormLabel htmlFor="password" sx={{mb: 1, fontWeight: 500}}>
                                        Password
                                    </FormLabel>
                                    <TextField
                                        error={passwordError}
                                        helperText={passwordErrorMessage}
                                        name="password"
                                        placeholder="••••••••"
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        autoComplete="new-password"
                                        required
                                        fullWidth
                                        variant="outlined"
                                        color={passwordError ? 'error' : 'primary'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Key/>
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOff/> : <Visibility/>}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }}
                                    />

                                    {/* Password strength indicator */}
                                    {password && (
                                        <Box>
                                            <PasswordStrengthIndicator strength={passwordStrength} />
                                        </Box>
                                    )}

                                    {/* Password requirements */}
                                    <PasswordRequirements
                                        requirements={passwordStrength.requirements}
                                        password={password}
                                    />
                                </StyledFormControl>

                                {/* Confirm Password */}
                                <StyledFormControl fullWidth>
                                    <FormLabel htmlFor="confirmPassword" sx={{mb: 1, fontWeight: 500}}>
                                        Confirm Password
                                    </FormLabel>
                                    <TextField
                                        error={confirmPasswordError}
                                        helperText={confirmPasswordErrorMessage}
                                        name="confirmPassword"
                                        placeholder="••••••••"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        id="confirmPassword"
                                        required
                                        fullWidth
                                        variant="outlined"
                                        color={confirmPasswordError ? 'error' : 'primary'}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Key/>
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle confirm password visibility"
                                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                        edge="end"
                                                    >
                                                        {showConfirmPassword ? <VisibilityOff/> : <Visibility/>}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }}
                                    />
                                </StyledFormControl>
                                {/* Date of Birth */}
                                <StyledFormControl fullWidth>
                                    <FormLabel htmlFor="dateOfBirth" sx={{mb: 1, fontWeight: 500}}>
                                        Date of Birth (required)
                                    </FormLabel>
                                    {!dateOfBirthError && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            You cannot create an account if you are under 16 years old.
                                        </Typography>
                                    )}
                                    <TextField
                                        id="dateOfBirth"
                                        name="dateOfBirth"
                                        type="date"
                                        required
                                        fullWidth
                                        variant="outlined"
                                        error={dateOfBirthError}
                                        helperText={dateOfBirthErrorMessage}
                                        color={dateOfBirthError ? 'error' : 'primary'}
                                        value={dateOfBirth}
                                        onChange={(e) => handleDateOfBirthChange(e.target.value)}
                                        FormHelperTextProps={{
                                            sx: dateOfBirthError ? { color: 'error.main' } : undefined,
                                        }}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }}
                                    />
                                </StyledFormControl>

                                {/* Sex */}
                                <StyledFormControl fullWidth>
                                    <FormLabel htmlFor="sex" sx={{mb: 1, fontWeight: 500}}>
                                        Sex (required)
                                    </FormLabel>
                                    <TextField
                                        id="sex"
                                        name="sex"
                                        select
                                        required
                                        fullWidth
                                        variant="outlined"
                                        error={sexError}
                                        helperText={sexErrorMessage}
                                        color={sexError ? 'error' : 'primary'}
                                        value={sex}
                                        onChange={(e) => setSex(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }}
                                    >
                                        <MenuItem value="">Select...</MenuItem>
                                        <MenuItem value="male">Male</MenuItem>
                                        <MenuItem value="female">Female</MenuItem>
                                        <MenuItem value="other">Other</MenuItem>
                                    </TextField>
                                </StyledFormControl>
                                {/* Address (optional) */}
                                <StyledFormControl fullWidth>
                                    <FormLabel htmlFor="address" sx={{mb: 1, fontWeight: 500}}>
                                        Address (optional)
                                    </FormLabel>
                                    <TextField
                                        id="address"
                                        name="address"
                                        placeholder="Your address"
                                        fullWidth
                                        variant="outlined"
                                        color="primary"
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Home/>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }}
                                    />
                                </StyledFormControl>
                            </Grid>
                        </Grid>

                        {/* Full width elements */}
                        <Box sx={{mt: 2, ml: 0}}>
                            {/* Terms and Conditions */}
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{
                                    mb: 3,
                                    flexDirection: {xs: 'column', sm: 'row'},
                                    alignItems: {xs: 'flex-start', sm: 'center'}
                                }}
                            >
                                <Box display="flex" alignItems="center">
                                    <Checkbox
                                        checked={acceptedTerms}
                                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                                        sx={{mr: 1}}
                                    />
                                    <Typography>
                                        I have read and understand Terms and Conditions
                                    </Typography>
                                </Box>
                                <Button
                                    variant="outlined"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        openTerms();
                                    }}
                                    size="small"
                                    sx={{
                                        ml: {xs: 0, sm: 0},
                                        mt: {xs: 1, sm: 0},
                                        borderRadius: '8px',
                                        textTransform: 'none'
                                    }}
                                >
                                    Open Terms
                                </Button>
                            </Box>

                            {turnstileSiteKey && (
                                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', minHeight: 70 }}>
                                    <Box ref={captchaContainerRef} />
                                    {!captchaReady && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                            Loading captcha...
                                        </Typography>
                                    )}
                                </Box>
                            )}


                            {!isSubmitting ? (
                                <StyledButton
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={isCreateAccountDisabled}
                                    sx={{mb: 3}}
                                >
                                    Create Account
                                </StyledButton>

                            ) : (
                                <StyledButton
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={true}
                                    color="inherit"
                                    sx={{mb: 3}}
                                >
                                    <CircularProgress size={24} color="inherit"/>
                                </StyledButton>

                            )}
                            {!isSubmitting && isCreateAccountDisabled && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 3, mt: -1, textAlign: 'center' }}
                                >
                                    {createAccountBlockerReason}
                                </Typography>
                            )}

                        </Box>

                        <Divider sx={{mb: 3}}>or</Divider>

                        <Box sx={{textAlign: 'center'}}>
                            <Typography>
                                Already have an account?{' '}
                                <Link to="/login" style={{
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                    textDecoration: 'none'
                                }}>
                                    Sign in
                                </Link>
                            </Typography>
                        </Box>
                    </Box>
                </Card>
                <ContractTermsModal
                    open={termsModalOpen}
                    onClose={() => setTermsModalOpen(false)}
                    termsId={termsId}
                />
            </SignUpContainer>
            <Dialog
                open={notificationOpen}
                onClose={() => setNotificationOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        padding: '8px',
                        maxWidth: '400px'
                    }
                }}
            >
                <DialogTitle sx={{
                    backgroundColor: '#f6ffed',
                    color: '#52c41a',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderRadius: '8px 8px 0 0'
                }}>
                    <CheckCircle color="success" />
                    Registration Successful
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <DialogContentText>
                        {notificationMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setNotificationOpen(false);
                            navigate('/login');
                        }}
                        variant="contained"
                        color="primary"
                        autoFocus
                        sx={{
                            borderRadius: '25px',
                            fontWeight: 600,
                            textTransform: 'none',
                            backgroundColor: '#52c41a',
                            '&:hover': {
                                backgroundColor: '#389e0d'
                            }
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </AppTheme>

    );
}